import { KeysMatching, keysOf, KeysOfUnion, ValueOf } from '@pelicargo/types';
import { FilterExpressionSnippet, HandlingOptions, RuleType } from '@prisma/client';
import { difference } from 'lodash';
import { ZodFirstPartyTypeKind, ZodObjectDef, ZodRawShape, ZodTypeAny } from 'zod';

import {
  AccountModel,
  AircraftModel,
  AirportModel,
  CargoModel,
  FlightSegmentModel,
  LineItemModel,
  ProviderAccountModel,
  ProviderModel,
  QuoteModel,
  QuoteVariantModel,
  RequestModel,
  RuleModel,
} from '../../../api/src/lib/zod-prisma';
import { getRealType, StripWrappers } from './zod';

type SubObjects<Source extends DataSources> = KeysMatching<RawDataTypes[Source], DataType.SubObject>;

type ListSubObjects<Source extends DataSources> = KeysMatching<RawDataTypes[Source], DataType.ListSubObject>;

type AllSubObjects<Source extends DataSources> = SubObjects<Source> | ListSubObjects<Source>;

type RelationCheck = {
  [Id in DataSources]: {
    [K in AllSubObjects<Id>]: DataSources;
  };
};

export enum SCOPES {
  requestClassification = 'requestClassification',
  moveToInProgress = 'moveToInProgress',
  shouldSearchForSystemFPCs = 'shouldSearchForSystemFPCs',
  moveToQuotingComplete = 'moveToQuotingComplete',
  quoteSelector = 'quoteSelector',
  shouldHandleFirstParty = 'shouldHandleFirstParty',
  sellRateLineItems = 'sellRateLineItems',
  airlineBookingWarning = 'airlineBookingWarning',
  conditionalFields = 'conditionalFields',
}

export enum DataType {
  String = 'string',
  Number = 'number',
  Enum = 'enum',
  Boolean = 'bool', // as the type name should be
  SubObject = 'subObject',
  Date = 'date',
  Reference = 'reference',

  ListString = 'listString',
  ListNumber = 'listNumber',
  ListEnum = 'listEnum',
  ListBoolean = 'listBoolean',
  ListSubObject = 'listSubObject',

  // fallback
  Unsupported = 'unsupported',
}

export type DataTypeToRealType = {
  [DataType.Boolean]: boolean;
  [DataType.String]: string;
  [DataType.Enum]: string;
  [DataType.Number]: number;
  [DataType.SubObject]: Record<string, DataType>;
  [DataType.Date]: Date;
  [DataType.Reference]: boolean;

  [DataType.ListString]: string[];
  [DataType.ListBoolean]: boolean[];
  [DataType.ListNumber]: number[];
  [DataType.ListEnum]: string[];
  [DataType.ListSubObject]: Record<string, DataType>[];

  [DataType.Unsupported]: unknown;
};

type ZodTypeToDataType = {
  [ZodFirstPartyTypeKind.ZodNumber]: DataType.Number;
  [ZodFirstPartyTypeKind.ZodBoolean]: DataType.Boolean;
  [ZodFirstPartyTypeKind.ZodEnum]: DataType.Enum;
  [ZodFirstPartyTypeKind.ZodNativeEnum]: DataType.Enum;
  [ZodFirstPartyTypeKind.ZodDate]: DataType.Date;
  [ZodFirstPartyTypeKind.ZodString]: DataType.String;
  [ZodFirstPartyTypeKind.ZodObject]: DataType.SubObject;
};

type ZodArrayToListType = {
  [ZodFirstPartyTypeKind.ZodNumber]: DataType.ListNumber;
  [ZodFirstPartyTypeKind.ZodBoolean]: DataType.ListBoolean;
  [ZodFirstPartyTypeKind.ZodEnum]: DataType.ListEnum;
  [ZodFirstPartyTypeKind.ZodNativeEnum]: DataType.ListEnum;
  [ZodFirstPartyTypeKind.ZodString]: DataType.ListString;
  [ZodFirstPartyTypeKind.ZodObject]: DataType.ListSubObject;
};

type ConvertZodToPeli<T extends ZodTypeAny> = StripWrappers<T>['_def']['typeName'] extends ZodFirstPartyTypeKind
  ? StripWrappers<T>['_def']['typeName'] extends ZodFirstPartyTypeKind.ZodArray
    ? ZodArrayToListType[StripWrappers<T['_def']['type']>['_def']['typeName']]
    : ZodTypeToDataType[StripWrappers<T>['_def']['typeName']]
  : DataType.Unsupported;

const generateSource = <T extends ZodRawShape>(modelName: string, def: ZodObjectDef<T>) => {
  const shape = def.shape();
  const enums: Record<string, string[]> = {};

  const source: {
    [Id in keyof typeof shape]: ConvertZodToPeli<(typeof shape)[Id]>;
  } = {} as any;

  keysOf(shape).forEach((attr) => {
    const [name, def] = getRealType(shape[attr]);
    // Fallback
    source[attr] = DataType.Unsupported;
    switch (name) {
      case 'ZodString':
        source[attr] = DataType.String as any;
        return;
      case 'ZodNumber':
        source[attr] = DataType.Number as any;
        return;
      case 'ZodNativeEnum':
        enums[`${modelName}.${String(attr)}`] = Object.values(def?.['values'] || {});
        source[attr] = DataType.Enum as any;
        return;
      case 'ZodBoolean':
        source[attr] = DataType.Boolean as any;
        return;
      case 'ZodDate':
        source[attr] = DataType.Date as any;
        return;
      case 'ZodArray': {
        const [arrayName, def] = getRealType(shape[attr]._def['type']);
        switch (arrayName) {
          case 'ZodString':
            source[attr] = DataType.ListString as any;
            return;
          case 'ZodNumber':
            source[attr] = DataType.ListNumber as any;
            return;
          case 'ZodNativeEnum':
            enums[`${modelName}.${String(attr)}`] = Object.values(def?.['values'] || {});
            source[attr] = DataType.ListEnum as any;
            return;
        }
        break;
      }
    }
  });
  return { source, enums };
};

const requestShape = RequestModel._def;
const { source: realRequestSource, enums: requestEnums } = generateSource('request', requestShape);

const quoteShape = QuoteModel._def;
const { source: realQuoteSource, enums: quoteEnums } = generateSource('quote', quoteShape);

const quoteVariantShape = QuoteVariantModel._def;
const { source: realQuoteVariantSource, enums: quoteVariantEnums } = generateSource('quoteVariant', quoteVariantShape);

const lineItemShape = LineItemModel._def;
const { source: lineItemSource, enums: lineItemEnums } = generateSource('lineItem', lineItemShape);

const cargoShape = CargoModel._def;
const { source: cargoSource, enums: cargoEnums } = generateSource('cargo', cargoShape);

const flightSegmentShape = FlightSegmentModel._def;
const { source: realFlightSegmentSource, enums: flightSegmentEnums } = generateSource(
  'flightSegment',
  flightSegmentShape,
);

const ruleShape = RuleModel._def;
const { source: ruleSource, enums: ruleEnums } = generateSource('rule', ruleShape);

const accountShape = AccountModel._def;
const { source: realAccountSource, enums: accountEnums } = generateSource('account', accountShape);

const airportShape = AirportModel._def;
const { source: airportSource, enums: airportEnums } = generateSource('airport', airportShape);

const aircraftShape = AircraftModel._def;
const { source: aircraftSource, enums: aircraftEnums } = generateSource('aircraft', aircraftShape);

const providerAccountShape = ProviderAccountModel._def;
const { source: providerAccountSource, enums: providerAccountEnums } = generateSource(
  'providerAccount',
  providerAccountShape,
);

const providerShape = ProviderModel._def;
const { source: providerSource, enums: providerEnums } = generateSource('provider', providerShape);

export const requestSource = {
  ...realRequestSource,
  total_weight: DataType.Number,
  max_weight: DataType.Number,
  max_width: DataType.Number,
  max_height: DataType.Number,
  max_length: DataType.Number,
  destination: DataType.ListString,
  origin: DataType.ListString,
  total_volume: DataType.Number,
  max_volume: DataType.Number,
  percent_quotes_fulfilled: DataType.Number,
  quote_count: DataType.Number,
  cargo: DataType.ListSubObject,
  account: DataType.SubObject,
  inland_quote_variants: DataType.ListSubObject,
  cargo_volume: DataType.Number,

  // In stasis - currently not in a situation where this matters
  //  minutes_as_current_status: DataType.Number,
} as const;

export const quoteSource = {
  ...realQuoteSource,
  has_complete_flight_segments: DataType.Boolean,

  provider: DataType.SubObject,
  flight_path: DataType.ListSubObject,
  quote_variants: DataType.ListSubObject,
  origin_airport: DataType.SubObject,
  destination_airport: DataType.SubObject,

  // In stasis - currently not in a situation where this matters
  //  minutes_as_current_status: DataType.Number,
} as const;

export const quoteVariantSource = {
  ...realQuoteVariantSource,
  line_items: DataType.ListSubObject,
} as const;

export const accountSource = {
  ...realAccountSource,
  provider_accounts: DataType.ListSubObject,
} as const;

export const flightSegmentSource = {
  ...realFlightSegmentSource,
  origin_airport: DataType.SubObject,
  destination_airport: DataType.SubObject,
  aircraft: DataType.SubObject,
} as const;

// pre-contextification
export const rawDataSource = {
  request: requestSource,
  quote: quoteSource,
  quoteVariant: quoteVariantSource,
  lineItem: lineItemSource,
  cargo: cargoSource,
  flightSegment: flightSegmentSource,
  account: accountSource,
  airport: airportSource,
  aircraft: aircraftSource,
  provider: providerSource,

  providerAccount: providerAccountSource,

  // This is for handling a special case - filters aren't compared to directly, but you can reference what their value would be
  filter: {
    matches: DataType.Boolean,
  },
  // Same as above, with a slightly different path
  snippet: {
    matches: DataType.Boolean,
  },

  // Second special case, globals!
  global: {},

  rule: ruleSource,
} as const;

export type RawDataTypes = typeof rawDataSource;
export type DataSources = keyof RawDataTypes;

export type ProcessedDataSource = {
  [Source in DataSources]: RawDataTypes[Source] & {
    [Key in Extract<
      KeysMatching<RawDataTypes[Source], DataType.ListSubObject>,
      string
    > as `$${Key}__count`]: DataType.Number;
  };
};

// post-contextification
export const dataSource = keysOf(rawDataSource).reduce(
  (agg, source) => {
    for (const key in agg[source]) {
      if (rawDataSource[source][key] === DataType.ListSubObject) {
        agg[source][`$${key}__count`] = DataType.Number;
      }
    }
    return agg;
  },
  structuredClone(rawDataSource) as ProcessedDataSource,
);

export const scopeSources = {
  requestClassification: ['request'],
  moveToInProgress: ['request', 'quote'],
  shouldSearchForSystemFPCs: ['request', 'quote'],
  moveToQuotingComplete: ['request'],
  quoteSelector: ['request', 'quote'],
  shouldHandleFirstParty: ['request'],
  sellRateLineItems: ['quote', 'quoteVariant', 'lineItem'],
  airlineBookingWarning: ['request', 'quote', 'quoteVariant'],
  conditionalFields: ['request', 'quote'],
} as const satisfies Record<SCOPES, DataSources[]>;

export const scopeReferenceable = keysOf(scopeSources).reduce(
  (agg, scope) => {
    agg[scope] = (keysOf(scopeSources) as SCOPES[]).filter((subScope: SCOPES) => {
      // The cast here is actually due to TS being too over-specific when it comes to const arrays
      return scopeSources[subScope].every((val) => (scopeSources[scope] as DataSources[]).includes(val));
    });
    return agg;
  },
  {} as Record<SCOPES, SCOPES[]>,
);

export const referenceToValues = (reference: string) => {
  const [scope, ...splitIdentifier] = reference.split(' - ');
  const identifier = splitIdentifier.join(' - ');
  return { scope, identifier };
};

type VariableSource = {
  name: string;
  prerequisites: DataSources[];
  description: string;
  // TODO: account for more cases
  discriminators: Record<
    string,
    {
      label: string;
      options: string[];
    }
  >;
};

export const variableSource = {
  rule: ruleSource,
} as const;

export type VariableSources = typeof variableSource;

export const variableSourceInfo: Record<keyof typeof variableSource, VariableSource> = {
  rule: {
    name: 'Rules Data',
    description: 'Based off Rules table. Standard requires',
    // Request for the account id, quote for the provider
    prerequisites: ['quote', 'request'],
    discriminators: {
      type: {
        label: 'Rule Type',
        options: Object.values(RuleType),
      },
      selectionType: {
        label: 'Rule Selection Method',
        options: ['standard', 'simple'],
      },
    },
  },
};

export const relations = {
  request: {
    cargo: 'cargo',
    account: 'account',
    inland_quote_variants: 'quoteVariant',
  },
  quote: {
    provider: 'provider',
    flight_path: 'flightSegment',
    quote_variants: 'quoteVariant',
    origin_airport: 'airport',
    destination_airport: 'airport',
  },
  quoteVariant: {
    line_items: 'lineItem',
  },
  cargo: {},
  flightSegment: {
    aircraft: 'aircraft',
    destination_airport: 'airport',
    origin_airport: 'airport',
  },
  lineItem: {},
  rule: {},
  account: {
    provider_accounts: 'providerAccount',
  },
  filter: {},
  snippet: {},
  airport: {},
  providerAccount: {},
  aircraft: {},
  provider: {},
  global: {},
} as const satisfies RelationCheck;

type RelationSource<Source extends DataSources> = (typeof relations)[Source];

export type ExpandedType<Source extends DataSources, Types extends RawDataTypes | ProcessedDataSource> = Omit<
  Types[Source],
  AllSubObjects<Source>
> & {
  [Key in keyof RelationSource<Source>]: RelationSource<Source>[Key] extends DataSources
    ? (Key extends keyof RawDataTypes[Source] ? RawDataTypes[Source][Key] : never) extends DataType.ListSubObject
      ? ExpandedType<RelationSource<Source>[Key], Types>[]
      : ExpandedType<RelationSource<Source>[Key], Types>
    : never;
};

type Structured = {
  [id: string | symbol]: Record<string, DataType | Structured | Structured[]>;
};

type Convert<Struct> = {
  [Key in keyof Struct]: Struct[Key] extends DataType
    ? DataTypeToRealType[Struct[Key]]
    : Struct[Key] extends any[]
      ? Convert<Struct[Key][0]>[]
      : Struct[Key] extends Structured
        ? Convert<Struct[Key]>
        : unknown;
};

// wow, what a name
export type TypedTypeInternal<Source extends DataSources, Types extends RawDataTypes | ProcessedDataSource> = {
  [Key in keyof ExpandedType<Source, Types>]: ExpandedType<Source, Types>[Key] extends DataType
    ? DataTypeToRealType[ExpandedType<Source, Types>[Key]]
    : ExpandedType<Source, Types>[Key] extends any[]
      ? Convert<ExpandedType<Source, Types>[Key][0]>[]
      : Convert<ExpandedType<Source, Types>[Key]>;
};

export type TypedType<
  Source extends DataSources,
  ProcessPos extends 'precontext' | 'postcontext',
> = ProcessPos extends 'precontext'
  ? TypedTypeInternal<Source, RawDataTypes>
  : TypedTypeInternal<Source, ProcessedDataSource>;

export const enumValues = {
  ...requestEnums,
  ...quoteEnums,
  ...ruleEnums,
  ...quoteVariantEnums,
  ...lineItemEnums,
  ...cargoEnums,
  ...flightSegmentEnums,
  ...accountEnums,
  ...airportEnums,
  ...providerAccountEnums,
  ...aircraftEnums,
  ...providerEnums,
  ...providerAccountEnums,
  // Due to how things are handled, some unfortunately need to be specified
  'request.cargo_handling': Object.values(HandlingOptions),
};

export const groupOperations = {
  all: 'All of the following must be true',
  some: 'At least one of the following must be true',
  one: 'Only one of the following must be true',
  none: 'None of the following must be true',
};

export const groupOperationBorders: Record<keyof typeof groupOperations, string> = {
  all: '2px solid green',
  some: '2px dashed green',
  one: '2px dotted orange',
  none: '2px solid red',
};

export enum CheckType {
  group = 'group',
  data = 'data',
}

type LiteralComparison = {
  literal: string;
  subConditions?: GroupCondition;

  source?: never;
  variable?: never;
};

type VariableComparison = {
  literal?: never;
  subConditions?: GroupCondition;

  source?: string;
  variable?: string;
};

export type ValueArgs = {
  source: DataSources;
  value: string;
  comparison: LiteralComparison | VariableComparison;
};

export type ValueCondition = {
  type: CheckType.data;
  func: KeysOfUnion<ValueOf<typeof valueLogic>>;
  args: ValueArgs;
};

export type GroupCondition = {
  type: CheckType.group;
  func: keyof typeof groupLogic;
  label?: string;
  args: Condition[];
};

export type Condition = ValueCondition | GroupCondition;

export type LogicFunction<A1 extends DataType, A2 extends DataType> = {
  modelArg: A1;
  cmpArg: A2;
  static: boolean;
  fn: (a: DataTypeToRealType[A1], b: DataTypeToRealType[A2]) => boolean;
};
type AllPermutations = { [Arg1 in DataType]: { [Arg2 in DataType]: LogicFunction<Arg1, Arg2> } };
type AllPermutationFunctions = AllPermutations[DataType][DataType];

const logic = <A1 extends DataType, A2 extends DataType>(args: LogicFunction<A1, A2>): LogicFunction<A1, A2> => args;

// Not a type checker, just type Checker
type Checker<T> = (arg: T, i?: number) => boolean;

// List operations
const one: <T>(list: T[], checker: Checker<T>) => boolean = (list, checker) =>
  list.map(checker).filter((x) => x).length === 1;

const some: <T>(list: T[], checker: Checker<T>) => boolean = (list, checker) =>
  list.map(checker).filter((x) => x).length > 0;

const all: <T>(list: T[], checker: Checker<T>) => boolean = (list, checker) =>
  list.map(checker).filter((x) => x).length === list.length;

const none: <T>(list: T[], checker: Checker<T>) => boolean = (list, checker) =>
  list.map(checker).filter((x) => x).length === 0;

export const nullChecks = <T extends DataType, FakeCmp extends DataType>(type: T, fakeCmp: FakeCmp) =>
  ({
    isNullish: logic({
      modelArg: type,
      cmpArg: fakeCmp,
      static: true,
      fn: (a: any) => a === undefined || a === null,
    }),
    isDefined: logic({
      modelArg: type,
      cmpArg: fakeCmp,
      static: true,
      fn: (a: any) => a !== undefined && a !== null,
    }),
  }) as const;

// Logic that works with the concept of lists
const listLogic = <T extends DataType>(type: T) => ({
  item_count_greater_than: logic({
    modelArg: type,
    cmpArg: DataType.Number,
    static: false,
    fn: (list: DataTypeToRealType[T], b: number) =>
      numberLogic['greaterThan'].fn((list as any[]).length, parseInt(`${b}`)),
  }),

  item_count_less_than: logic({
    modelArg: type,
    cmpArg: DataType.Number,
    static: false,
    fn: (list: DataTypeToRealType[T], b: number) =>
      numberLogic['lessThan'].fn((list as any[]).length, parseInt(`${b}`)),
  }),

  item_count_equals: logic({
    modelArg: type,
    cmpArg: DataType.Number,
    static: false,
    fn: (list: DataTypeToRealType[T], b: number) => numberLogic['equals'].fn((list as any[]).length, parseInt(`${b}`)),
  }),

  is_subset_of: logic({
    modelArg: type,
    cmpArg: type,
    static: false,
    fn: (a: DataTypeToRealType[T], b: DataTypeToRealType[T]) => {
      return difference(a as any, b as any).length === 0;
    },
  }),

  is_superset_of: logic({
    modelArg: type,
    cmpArg: type,
    static: false,
    fn: (a: DataTypeToRealType[T], b: DataTypeToRealType[T]) => {
      return difference(b as any, a as any).length === 0;
    },
  } as const),
});

const numberLogic = {
  equals: logic({
    modelArg: DataType.Number,
    cmpArg: DataType.Number,
    static: false,
    fn: (a: number, b: number) => a === b,
  }),
  notEquals: logic({
    modelArg: DataType.Number,
    cmpArg: DataType.Number,
    static: false,
    fn: (a: number, b: number) => a !== b,
  }),
  lessThan: logic({
    modelArg: DataType.Number,
    cmpArg: DataType.Number,
    static: false,
    fn: (a: number, b: number) => a < b,
  }),
  lessThanOrEqual: logic({
    modelArg: DataType.Number,
    cmpArg: DataType.Number,
    static: false,
    fn: (a: number, b: number) => a <= b,
  }),
  greaterThan: logic({
    modelArg: DataType.Number,
    cmpArg: DataType.Number,
    static: false,
    fn: (a: number, b: number) => a > b,
  }),
  greaterThanOrEqual: logic({
    modelArg: DataType.Number,
    cmpArg: DataType.Number,
    static: false,
    fn: (a: number, b: number) => a >= b,
  }),
  ...nullChecks(DataType.Number, DataType.Number),
} as const satisfies Record<string, LogicFunction<DataType.Number, DataType.Number>>;

const stringLogic = {
  equals: logic({
    modelArg: DataType.String,
    cmpArg: DataType.String,
    static: false,
    fn: (a: string, b: string) => a === b,
  }),
  notEquals: logic({
    modelArg: DataType.String,
    cmpArg: DataType.String,
    static: false,
    fn: (a: string, b: string) => a !== b,
  }),
  contains: logic({
    modelArg: DataType.String,
    cmpArg: DataType.String,
    static: false,
    fn: (a: string, b: string) => a?.indexOf(b) !== -1,
  }),
  ...nullChecks(DataType.String, DataType.String),
} as const;

const listNumberLogic = {
  all: logic({
    modelArg: DataType.ListNumber,
    cmpArg: DataType.Number,
    static: false,
    fn: (list: number[], comparator: number) => all(list, (n) => numberLogic['equals'].fn(n, comparator)),
  }),
  some: logic({
    modelArg: DataType.ListNumber,
    cmpArg: DataType.Number,
    static: false,
    fn: (list: number[], comparator: number) => some(list, (n) => numberLogic['equals'].fn(n, comparator)),
  }),
  none: logic({
    modelArg: DataType.ListNumber,
    cmpArg: DataType.Number,
    static: false,
    fn: (list: number[], comparator: number) => none(list, (n) => numberLogic['equals'].fn(n, comparator)),
  }),
  ...listLogic(DataType.ListNumber),
} as const;

const listStringLogic = {
  all: logic({
    modelArg: DataType.ListString,
    cmpArg: DataType.String,
    static: false,
    fn: (list: string[], comparator: string) => all(list, (n) => stringLogic['equals'].fn(n, comparator)),
  }),
  some: logic({
    modelArg: DataType.ListString,
    cmpArg: DataType.String,
    static: false,
    fn: (list: string[], comparator: string) => some(list, (n) => stringLogic['equals'].fn(n, comparator)),
  }),
  none: logic({
    modelArg: DataType.ListString,
    cmpArg: DataType.String,
    static: false,
    fn: (list: string[], comparator: string) => none(list, (n: string) => stringLogic['equals'].fn(n, comparator)),
  }),
  ...listLogic(DataType.ListString),
} as const;

const booleanLogic = {
  equals: logic({
    modelArg: DataType.Boolean,
    cmpArg: DataType.Boolean,
    static: false,
    fn: (a: boolean, b: boolean) => a === b,
  }),
} as const;

const listBooleanLogic = {
  all: logic({
    modelArg: DataType.ListBoolean,
    cmpArg: DataType.Boolean,
    static: false,
    fn: (list: boolean[], comparator: boolean) => all(list, (n) => booleanLogic['equals'].fn(n, comparator)),
  }),
  some: logic({
    modelArg: DataType.ListBoolean,
    cmpArg: DataType.Boolean,
    static: false,
    fn: (list: boolean[], comparator: boolean) => {
      return some(list, (n) => booleanLogic['equals'].fn(n, comparator));
    },
  }),
  none: logic({
    modelArg: DataType.ListBoolean,
    cmpArg: DataType.Boolean,
    static: false,
    fn: (list: boolean[], comparator: boolean) => none(list, (n) => booleanLogic['equals'].fn(n, comparator)),
  }),
  ...listLogic(DataType.ListBoolean),
} as const;

const dateLogic = {
  isPast: logic({
    modelArg: DataType.Date,
    cmpArg: DataType.String,
    static: true,
    fn: (a: Date) => Date.now() > a?.getTime(),
  }),
  isFuture: logic({
    modelArg: DataType.Date,
    cmpArg: DataType.String,
    static: true,
    fn: (a: Date) => Date.now() < a?.getTime(),
  }),
  ...nullChecks(DataType.Date, DataType.Date),
} as const;

// Enums are just strings with different values, so we can autogen them
const enumLogic = (Object.keys(stringLogic) as (keyof typeof stringLogic)[]).reduce(
  (agg, key): Record<keyof typeof stringLogic, LogicFunction<DataType.Enum, DataType>> => ({
    ...agg,
    [key as keyof typeof stringLogic]: {
      ...stringLogic[key],
      modelArg: DataType.Enum,
      cmpArg: DataType.Enum,
    },
  }),
  {} as Record<keyof typeof stringLogic, LogicFunction<DataType.Enum, DataType>>,
);

const listEnumLogic = (Object.keys(listStringLogic) as (keyof typeof listStringLogic)[]).reduce(
  (agg, key): Record<keyof typeof listStringLogic, LogicFunction<DataType.ListEnum, DataType>> => {
    let cmpArg: DataType = listStringLogic[key].cmpArg;
    if (cmpArg === DataType.String) {
      cmpArg = DataType.Enum;
    } else if (cmpArg === DataType.ListString) {
      cmpArg = DataType.ListEnum;
    }

    return {
      ...agg,
      [key as keyof typeof listStringLogic]: {
        ...listStringLogic[key],
        modelArg: DataType.ListEnum,
        cmpArg,
      },
    };
  },
  {} as Record<keyof typeof listStringLogic, LogicFunction<DataType.ListEnum, DataType>>,
);

export const groupLogic = {
  all,
  some,
  one,
  none,
} as const;

export const valueLogic = {
  [DataType.Enum]: enumLogic,
  [DataType.String]: stringLogic,
  [DataType.Number]: numberLogic,
  [DataType.Boolean]: booleanLogic,
  [DataType.SubObject]: booleanLogic,
  [DataType.Date]: dateLogic,
  [DataType.Reference]: booleanLogic,

  [DataType.ListEnum]: listEnumLogic,
  [DataType.ListBoolean]: listBooleanLogic,
  [DataType.ListNumber]: listNumberLogic,
  [DataType.ListString]: listStringLogic,
  [DataType.ListSubObject]: listBooleanLogic,

  [DataType.Unsupported]: nullChecks(DataType.Unsupported, DataType.Unsupported),
} as const satisfies Record<DataType, Record<string, AllPermutationFunctions>>;

export const defaultValues: {
  [K in keyof typeof valueLogic]: {
    func: keyof (typeof valueLogic)[K];
    comparison?: string;
  };
} = {
  [DataType.Enum]: { func: 'equals', comparison: '' },
  [DataType.String]: { func: 'equals', comparison: '' },
  [DataType.Number]: { func: 'equals', comparison: '0' },
  [DataType.Boolean]: { func: 'equals', comparison: 'true' },
  [DataType.SubObject]: { func: 'equals', comparison: 'true' },
  [DataType.Date]: { func: 'isDefined', comparison: '' },
  [DataType.Reference]: { func: 'equals', comparison: 'true' },

  [DataType.ListEnum]: { func: 'all', comparison: '' },
  [DataType.ListBoolean]: { func: 'all', comparison: 'true' },
  [DataType.ListNumber]: { func: 'all', comparison: '' },
  [DataType.ListString]: { func: 'all', comparison: '' },
  [DataType.ListSubObject]: { func: 'some', comparison: 'true' },

  [DataType.Unsupported]: { func: 'isDefined', comparison: 'true' },
} as const;

export type CompiledValueType = {
  type: CheckType.data;
  func: KeysOfUnion<ValueOf<typeof valueLogic>>;
  args: ValueArgs;
};

export type CompiledGroupType = {
  type: CheckType.group;
  func: keyof typeof groupLogic;
  label?: string;

  args: CompiledType[];
};

export type CompiledType = CompiledValueType | CompiledGroupType;

// Utils

export const matchingSnippets = (snippetRepo: FilterExpressionSnippet[], sourceScope: DataSources[]) => {
  return snippetRepo?.filter((snip) => snip.sources.every((source) => sourceScope.includes(source))) ?? [];
};

export const gather = (filter: CompiledGroupType) => {
  const split = {
    group: [],
    data: [],
    ...Object.groupBy(filter.args, (filt) => filt.type),
  };
  for (const group of split.group) {
    // type shenanigans
    if (group.type === CheckType.group) {
      split.data.push(...gather(group));
    }
  }
  return split.data as CompiledValueType[];
};

export type Globals = {
  [source in DataSources]?: (keyof ProcessedDataSource[source])[];
};

export const findUsedGlobals = (group: CompiledGroupType) => {
  const groups = group.args.filter((cond) => cond.type === CheckType.group).flatMap(findUsedGlobals);
  const dataChecks = group.args.filter((cond) => cond.type === CheckType.data);
  const subobjects = dataChecks
    .filter((check) => !!check.args.comparison.subConditions)
    .map((check) => check.args.comparison.subConditions)
    .flatMap(findUsedGlobals);

  return [
    ...groups,
    ...subobjects,
    ...dataChecks.filter((cond) => cond.args.source === 'global').map((cond) => (cond as CompiledValueType).args.value),
  ];
};

export const buildAvailableGlobals = (sources: DataSources[]) => {
  return sources.flatMap((source) => Object.keys(dataSource[source]).map((attr) => `${source}.${attr}`));
};
